import styles from './TaskChatHeader.module.css'
import {Button, PageHeader, RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import type {Session} from '../../types/session'
import {CheckIcon, CommentIcon, CopilotIcon} from '@primer/octicons-react'
import type {Pull} from '../../types/pull'
import pluralize from 'pluralize'
import {useCurrentUser} from '../../contexts/CurrentUserContext'
import {useFeedbackDialog} from './feedback/FeedbackButton'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useAgentInfo} from '@github-ui/copilot-shared-ui/agents/use-agent-info'
import {useTaskAuthor} from '../../hooks/use-task-author'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {useSelectedModel} from '../../hooks/use-selected-model'
import type {HydratedAPITask} from '../../types/api-task'
import {TaskActions} from '../TaskActions'
import {getTaskTitle} from '../../utils/task-helpers'
import {useState, useRef, useMemo, useCallback} from 'react'
import {
  getLeadingVisualIconAttributes,
  getLeadingVisualStatusLabel,
} from '../../utils/get-leading-visual-icon-attributes'
import {Icon} from '@github-ui/pacer/Icon'
import {isCopilotCodingAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {useAgentProfile} from '../../contexts/agent-profile-context'
import {ChecksStatusButton} from './ChecksStatusButton'
import {OpenInMenuV2} from './OpenInMenuV2'
import {PullRequestButton} from './PullRequestButton'
import {getBranchArtifactData, getEffectivePrState, getGitHubArtifactData} from '../../utils/task-artifact-helpers'
import {RenameTaskHeader} from './RenameTaskHeader'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {TaskActionItem} from '../../hooks/use-task-action-items'
import {useIsLayoutNarrow} from './use-is-layout-narrow'
import {useIsAhpSession} from '../../hooks/use-is-ahp-session'
import {defaultWorkingDirectory} from '@github-ui/ahp-relay/session-host/create-session'
import {toAiCredits} from '@github-ui/ahp-relay/session-usage'
import {EnvironmentInfo} from './EnvironmentInfo'
import {TaskDebugLogButton} from './TaskDebugLogButton'
import {sumSessionSpendNanoAiu} from '../../utils/session-usage'
import {isQuickChatTask} from '../../utils/is-quick-chat-task'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {AgentsWorkbenchToggle} from '../workbench/AgentsWorkbenchToggle'
import {getEnvironmentFreshness, useAnnounceEnvironmentDetails} from '../../hooks/use-environment-details-visibility'
import {ENVIRONMENT_STATUS_LABELS} from '@github-ui/copilot-shared-ui/environments/environment-display'

interface TaskChatHeaderProps {
  sessions: Session[]
  currentSessionId?: string
  pull?: Pull
  task?: HydratedAPITask
  repoOwner?: string
  repoName?: string
  hideArchive?: boolean
  hideActions?: boolean
  hideEnvironmentVisual?: boolean
  isCodeReviewMode?: boolean
  dataUpdatedAt?: number
  onUserRename?: (title: string) => void
  chatApiUrl?: string
  useSidebarPageLayoutHeader?: boolean
}

export function TaskChatHeader({
  sessions,
  currentSessionId,
  pull,
  task,
  repoOwner,
  repoName,
  hideArchive = false,
  hideActions = false,
  hideEnvironmentVisual = false,
  isCodeReviewMode = false,
  dataUpdatedAt,
  onUserRename,
  chatApiUrl,
  useSidebarPageLayoutHeader = false,
}: TaskChatHeaderProps) {
  const mostRecentSession = sessions[sessions.length - 1]
  const firstSession = sessions[0]
  const taskCreatedAt = sessions.length > 0 ? firstSession?.created_at : null
  const premiumRequests = sessions.reduce((total, session) => total + (session.premium_requests || 0), 0)
  const {data: agentInfo} = useAgentInfo(mostRecentSession?.agent_id ?? null, mostRecentSession?.agent_slug)
  const agentProfile = useAgentProfile()
  const taskAuthor = useTaskAuthor(task, repoOwner, repoName)
  const taskScope = taskAuthor.isAutomation ? undefined : task?.compute?.scope
  const taskOwnerLogin = taskScope ?? taskAuthor.displayName
  const modelId = mostRecentSession?.model
  const isCopilotAgent = isCopilotCodingAgent(agentInfo?.slug)
  const canShowModel = copilotFeatureFlags.codingAgentThirdPartyModelUI || isCopilotAgent
  const resolvedModel = useSelectedModel(modelId)
  const selectedModel = canShowModel ? resolvedModel : undefined
  const {currentUser, optedInToFeedback} = useCurrentUser()
  const currentUserIsOwner = currentUser?.id === task?.creator?.id
  const separator = ' · '
  const isNarrow = useIsLayoutNarrow()
  const {openFeedback, FeedbackDialog} = useFeedbackDialog(mostRecentSession?.id, currentUser?.login)
  const feedbackButtonRef = useRef<HTMLButtonElement>(null)
  const handleOpenFeedback = useCallback(() => openFeedback(), [openFeedback])

  // AHP session environment info shown in the header. `environment` resolves via
  // the environments query, so the block only renders once it is available. The
  // working directory is only meaningful for `user-local` environments, where
  // the daemon runs against a directory on the user's own machine.
  const [environmentDetailsVisible, setEnvironmentDetailsVisible] = useState(false)
  const {
    isAhpSession,
    environment,
    error: environmentError,
    refetchEnvironment,
  } = useIsAhpSession(mostRecentSession ?? null, {observeEnvironment: environmentDetailsVisible})
  const environmentFreshness = getEnvironmentFreshness({
    hasEnvironment: !!environment,
    loading: false,
    error: !!environmentError,
  })
  const environmentAnnouncement = environment
    ? [`Environment status: ${ENVIRONMENT_STATUS_LABELS[environment.status]}`, environmentFreshness]
        .filter(Boolean)
        .join('. ')
    : undefined
  useAnnounceEnvironmentDetails(environmentDetailsVisible, environmentAnnouncement)
  const workingDirectory = useMemo(
    () => (environment?.kind === 'user-local' ? defaultWorkingDirectory() : undefined),
    [environment?.kind],
  )
  const showEnvironmentInfo = !hideEnvironmentVisual && isAhpSession && !!environment

  const handleToggleEnvironmentDetails = useCallback(
    (nextExpanded: boolean) => {
      setEnvironmentDetailsVisible(nextExpanded)
      if (nextExpanded) void refetchEnvironment?.()
      sendEvent('agent_sessions.task_view', {
        target: 'ENVIRONMENT_INFO_DETAILS',
        action: nextExpanded ? 'EXPAND' : 'COLLAPSE',
        task_id: task?.id,
        environment_id: environment?.id,
      })
    },
    [task?.id, environment?.id, refetchEnvironment],
  )

  const currentSession = currentSessionId ? sessions.find(session => session.id === currentSessionId) : undefined
  const sessionForBilling = currentSession ?? mostRecentSession
  const isAiCreditBilling = sessionForBilling?.usage?.type === 'ai_credits'
  const sessionSpendNanoAiu = isAiCreditBilling ? sumSessionSpendNanoAiu(sessions) : undefined
  const aiCredits =
    sessionSpendNanoAiu === undefined ? undefined : Math.round(toAiCredits(sessionSpendNanoAiu) * 10) / 10
  const usageAmount = isAiCreditBilling ? aiCredits : premiumRequests
  const usageUnit = isAiCreditBilling ? 'AI credits' : 'premium requests'

  const feedbackActionItem = useMemo((): TaskActionItem[] => {
    if (!optedInToFeedback || !isNarrow) return []
    return [
      {
        key: 'give-feedback',
        icon: CommentIcon,
        label: 'Give feedback',
        onSelect: () => openFeedback(),
      },
    ]
  }, [optedInToFeedback, isNarrow, openFeedback])

  const extraItems = useMemo(() => [...feedbackActionItem], [feedbackActionItem])

  const [isRenaming, setIsRenaming] = useState(false)
  const moreActionsButtonRef = useRef<HTMLButtonElement>(null)

  const taskTitle = getTaskTitle(task, pull?.title, {
    preferPullTitle: isCodeReviewMode,
    formatAsReview: isCodeReviewMode,
  })

  const handleExitRename = () => {
    setIsRenaming(false)
    requestAnimationFrame(() => {
      moreActionsButtonRef.current?.focus()
    })
  }

  const handleStartRename = () => {
    setIsRenaming(true)
    sendEvent('agent_sessions.task_actions', {
      target: 'RENAME_TASK_START',
      task_id: task?.id,
    })
  }

  const effectiveBranchName = getBranchArtifactData(task?.artifacts)?.head_ref || pull?.head_ref_name || ''

  const LeadingIcon = agentProfile.slots.LeadingIcon
  const artifactState = getGitHubArtifactData(task?.artifacts)?.state
  const effectivePrStatus = getEffectivePrState(artifactState, pull)
  const iconAttributes = useMemo(
    () =>
      LeadingIcon
        ? null
        : getLeadingVisualIconAttributes(
            effectivePrStatus,
            pull?.inMergeQueue,
            mostRecentSession?.state,
            !!task?.archived_at,
            mostRecentSession?.agent_slug,
          ),
    [
      effectivePrStatus,
      pull?.inMergeQueue,
      mostRecentSession?.state,
      mostRecentSession?.agent_slug,
      task?.archived_at,
      LeadingIcon,
    ],
  )

  const getTaskAuthorLogin = () => {
    if (taskScope) {
      return taskScope
    }

    if (!taskAuthor.isAutomation && currentUserIsOwner) {
      return 'You'
    }

    return taskOwnerLogin
  }

  if (isRenaming) {
    return (
      <RenameTaskHeader
        taskName={task?.name ?? 'New task'}
        displayTitle={taskTitle}
        taskId={task?.id}
        repoOwner={repoOwner}
        repoName={repoName}
        onExit={handleExitRename}
        onUserRename={onUserRename}
        apiUrl={chatApiUrl}
      />
    )
  }

  const actions = (
    <>
      {useSidebarPageLayoutHeader && (
        <SidebarPageLayout.SidebarTrigger className={styles.sidebarTrigger} variant="default" />
      )}
      {optedInToFeedback && !isNarrow && (
        <Button ref={feedbackButtonRef} variant="link" className="f6" onClick={handleOpenFeedback}>
          Give feedback
        </Button>
      )}
      <FeedbackDialog returnFocusRef={isNarrow ? moreActionsButtonRef : feedbackButtonRef} />
      <TaskDebugLogButton taskId={task?.id ?? mostRecentSession?.task_id} sessionId={mostRecentSession?.id} />
      {!hideActions && (
        <TaskActions
          enablePinning
          extraItems={extraItems}
          ariaLabel="More actions"
          testId="more-actions-button"
          overlayAlign="end"
          anchorRef={moreActionsButtonRef}
          state={mostRecentSession?.state}
          taskId={task?.id}
          repoOwner={repoOwner}
          repoName={repoName}
          currentUserIsOwner={currentUserIsOwner}
          archivedAt={task?.archived_at}
          eventType={firstSession?.event_type}
          eventIdentifiers={firstSession?.event_identifiers}
          pull={pull}
          hideCopy={agentProfile.privateSessions}
          hideShare={!agentProfile.privateSessions}
          hideArchive={hideArchive}
          interactiveSession={agentProfile.interactiveSession}
          agentSlug={agentProfile.slug}
          isQuickChatTask={isQuickChatTask(task)}
          hidePull={isCodeReviewMode}
          onRename={handleStartRename}
          sharingStatus={task?.sharing_status}
          automationId={task?.automation_id}
        />
      )}
      {isCodeReviewMode ? (
        pull?.url ? (
          <PullRequestButton pullUrl={pull.url} taskId={task?.id} repoNwo={pull.repository_nwo} />
        ) : null
      ) : (
        <>
          {effectiveBranchName && (
            <OpenInMenuV2
              branchName={effectiveBranchName}
              pull={pull}
              taskId={task?.id}
              isCurrentUserTaskCreator={currentUserIsOwner}
            />
          )}
        </>
      )}
      <AgentsWorkbenchToggle />
    </>
  )
  const narrowActionsRow = (
    <div className={clsx(styles.narrowActionsRow, useSidebarPageLayoutHeader && styles.narrowActionsRowFullWidth)}>
      {actions}
    </div>
  )
  const Header = PageHeader

  return (
    <>
      {isNarrow ? narrowActionsRow : null}
      <Header aria-label="Agent session header">
        <Header.TitleArea className={styles.headerTitleArea}>
          <Header.Title as="h1" className={styles.headerTitle}>
            {taskTitle}
          </Header.Title>
        </Header.TitleArea>
        <Header.Description>
          <div className={styles.headerDescriptionContent}>
            <div className={styles.headerStatus}>
              {LeadingIcon ? (
                <LeadingIcon
                  state={mostRecentSession?.state}
                  prState={pull?.state}
                  prInMergeQueue={pull?.inMergeQueue}
                  isArchived={!!task?.archived_at}
                  session={mostRecentSession}
                  staleAt={task?.stale_at}
                  dataUpdatedAt={dataUpdatedAt}
                  sharingStatus={task?.sharing_status}
                />
              ) : (
                <>
                  {iconAttributes && iconAttributes.icon !== CheckIcon && (
                    <span data-testid={iconAttributes['data-testid']}>
                      <Icon icon={iconAttributes.icon} color={iconAttributes.color} />
                    </span>
                  )}
                  {mostRecentSession && (
                    <span className="sr-only">
                      {getLeadingVisualStatusLabel(
                        effectivePrStatus,
                        pull?.inMergeQueue,
                        mostRecentSession.state,
                        !!task?.archived_at,
                      )}
                    </span>
                  )}
                </>
              )}
              <span className={styles.headerUser}>{getTaskAuthorLogin()}</span>
              <span>created the session</span>
              {taskCreatedAt && <RelativeTime date={new Date(taskCreatedAt)} tense="past" />}
              {(selectedModel || !isAhpSession) && <span>{separator}</span>}
              {selectedModel && (
                <span className={styles.modelDisplay}>
                  {selectedModel.logoURL ? (
                    <GitHubAvatar
                      alt={`${selectedModel.vendor} logo`}
                      square
                      size={18}
                      src={selectedModel.logoURL}
                      className={styles.modelIcon}
                    />
                  ) : (
                    <CopilotIcon className={styles.modelIcon} />
                  )}
                  <span>
                    {selectedModel.displayName}
                    {!isAhpSession && separator}
                  </span>
                </span>
              )}
              {!isAhpSession && (
                <span>
                  {sessions.length} {pluralize('session', sessions.length)}
                </span>
              )}
              {usageAmount !== undefined && usageAmount > 0 && (
                <span>
                  {separator}
                  {usageAmount} {usageUnit}
                </span>
              )}
              {pull?.head_sha && pull?.repository_nwo && (
                <span style={{marginLeft: 'var(--base-size-4)'}}>
                  <ChecksStatusButton
                    headSha={pull.head_sha}
                    repositoryNwo={pull.repository_nwo}
                    commitAliveChannel={pull.commit_alive_channel}
                  />
                </span>
              )}
              {showEnvironmentInfo && (
                <span className={styles.environmentInfoWrapper}>
                  <span>on</span>
                  <EnvironmentInfo
                    environment={environment}
                    workingDirectory={workingDirectory}
                    statusFreshness={environmentFreshness}
                    onToggleDetails={handleToggleEnvironmentDetails}
                  />
                </span>
              )}
            </div>
          </div>
        </Header.Description>
        {!isNarrow && <Header.Actions>{actions}</Header.Actions>}
      </Header>
    </>
  )
}
